import type { HTMLAttributes, ReactNode } from 'react';
import { cn } from '@/lib/utils/cn';
import { Container } from '@/components/ui/Container';
import { Eyebrow } from '@/components/ui/Eyebrow';

interface PageHeaderProps extends Omit<HTMLAttributes<HTMLElement>, 'title'> {
  /** Texto do eyebrow (kicker mono uppercase). Renderiza componente <Eyebrow>. */
  eyebrow?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
}

/**
 * PageHeader compacto (Aura DS v3 + filosofia Apple/Linear).
 *
 * Mudanças vs versão anterior:
 *  - py-6 desktop / py-5 mobile (era py-10 md:py-14 — ocupava 30% da viewport mobile)
 *  - title text-2xl md:text-3xl (era 3xl/4xl — exagerado em app interno)
 *  - description max-w-xl text-sm (era max-w-2xl text-base/lg)
 *  - eyebrow agora usa componente <Eyebrow> (com régua signal)
 */
export function PageHeader({
  eyebrow,
  title,
  description,
  actions,
  className,
  ...rest
}: PageHeaderProps) {
  return (
    <header
      className={cn('border-b border-border bg-surface py-5 md:py-6', className)}
      {...rest}
    >
      <Container>
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between md:gap-6">
          <div className="flex flex-col gap-2">
            {eyebrow ? <Eyebrow>{eyebrow}</Eyebrow> : null}
            <h1 className="text-2xl font-semibold tracking-tight text-ink md:text-3xl">
              {title}
            </h1>
            {description ? (
              <p className="max-w-xl text-sm text-muted">{description}</p>
            ) : null}
          </div>
          {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
        </div>
      </Container>
    </header>
  );
}
